import clsx from "clsx";

export function cn(...inputs: (string | boolean | undefined | null)[]) {
  return clsx(inputs);
}

const VPM_LOCALE = "en-US";

const VPM_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: "long",
  day: "numeric",
  year: "numeric",
};

/** Parse YYYY-MM-DD (or ISO datetime) for stable local display. */
function parseDisplayDate(input: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return new Date(`${input}T12:00:00`);
  }
  return new Date(input);
}

/** Month day, year — e.g. July 2, 2026 */
export function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  const date = parseDisplayDate(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString(VPM_LOCALE, VPM_DATE_FORMAT);
}

/** Month day, year range — e.g. July 1 – July 31, 2026 */
export function formatDateRange(from: string, to: string): string {
  if (!from && !to) return "";
  if (from === to) return formatDate(from);

  const fromDate = parseDisplayDate(from);
  const toDate = parseDisplayDate(to);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return `${from} – ${to}`;
  }

  if (fromDate.getFullYear() === toDate.getFullYear()) {
    const fromPart = fromDate.toLocaleDateString(VPM_LOCALE, {
      month: "long",
      day: "numeric",
    });
    return `${fromPart} – ${formatDate(to)}`;
  }

  return `${formatDate(from)} – ${formatDate(to)}`;
}

/** Month day, year with time — e.g. July 2, 2026, 3:45 PM */
export function formatDateTime(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(VPM_LOCALE, {
    ...VPM_DATE_FORMAT,
    hour: "numeric",
    minute: "2-digit",
  });
}

export function priorityColor(priority: string) {
  switch (priority) {
    case "high":
      return "text-vpm-red bg-red-50 ring-red-200";
    case "medium":
      return "text-vpm-amber bg-amber-50 ring-amber-200";
    default:
      return "text-slate-600 bg-slate-100 ring-slate-200";
  }
}

export function statusLabel(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
